import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild, forwardRef, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { ClickOutsideDirective, MenuPositionDirective, OverlayPortalDirective } from '@newroom-connect/library/directives';
import { ObjectHelper } from '@newroom-connect/library/helpers';

import { IconComponent } from '../../icon/icon.component';
import { InputComponent } from '../input.component';

interface INormalizedNestedOption {
  label: string;
  value: unknown;
  children: INormalizedNestedOption[];
}

interface IFlattenedNestedOption {
  value: unknown;
  path: string[];
}

interface IMenuLevel {
  option: INormalizedNestedOption;
  path: string[];
  triggerElement: HTMLElement;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IconComponent,
    ClickOutsideDirective,
    MenuPositionDirective,
    OverlayPortalDirective
  ],
  selector: 'nrc-input-nested-select',
  templateUrl: './input-nested-select.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputNestedSelectComponent),
      multi: true
    }
  ]
})
export class InputNestedSelectComponent extends InputComponent implements OnInit, OnChanges {
  private static readonly DISPLAY_SEPARATOR = ' → ';
  @Input() public options: unknown[] = [];
  @Input() public valueProperty?: string;
  @Input() public labelProperty?: string;
  @Input() public childrenProperty = 'children';
  @Input() public isTransparent = false;
  @Input() public isRound = false;
  @Input() public fitContent = false;
  @Input() public overflowParentElement?: HTMLElement;

  @ViewChild('selectInputElement') public selectInputElementRef!: ElementRef<HTMLDivElement>;

  public showOptions = signal<boolean>(false);
  public selectedPathLabels = signal<string[]>([]);
  public normalizedOptions: INormalizedNestedOption[] = [];
  public openMenus = signal<IMenuLevel[]>([]);

  private flattenedOptions: IFlattenedNestedOption[] = [];

  public override ngOnInit(): void {
    super.ngOnInit();
    this.rebuildOptions();
    this.syncSelectedPathFromValue(this.formControl?.value);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.rebuildOptions();
      this.syncSelectedPathFromValue(this.formControl?.value);
    }
  }

  public override writeValue(value: unknown): void {
    this.syncSelectedPathFromValue(value);
  }

  public toggleShowOptions(): void {
    if (this.disabled) {
      return;
    }

    this.showOptions.update(value => !value);
    if (!this.showOptions()) {
      this.clearOpenMenus();
    }
  }

  public closeOptions(): void {
    this.showOptions.set(false);
    this.clearOpenMenus();
  }

  public selectOption(option: INormalizedNestedOption, pathLabels: string[]): void {
    this.selectedPathLabels.set(pathLabels);

    if (this.formControl) {
      this.formControl.setValue(option.value);
      this.formControl.markAsDirty();
      this.formControl.markAsTouched();
    }

    this.showOptions.set(false);
    this.clearOpenMenus();
  }

  public handleOptionClick(
    option: INormalizedNestedOption,
    pathLabels: string[],
    event: MouseEvent,
    levelIndex: number
  ): void {
    if (option.children.length > 0) {
      event.stopPropagation();

      const currentMenus = this.openMenus();
      const currentMenuAtLevel = currentMenus[levelIndex];
      const isSameOption = currentMenuAtLevel?.option === option;
      const nextMenus = currentMenus.slice(0, levelIndex);

      if (!isSameOption) {
        nextMenus.push({
          option,
          path: pathLabels,
          triggerElement: event.currentTarget as HTMLElement
        });
      }

      this.openMenus.set(nextMenus);
      return;
    }

    this.selectOption(option, pathLabels);
  }

  public getDisplayValue(): string {
    const pathLabels = this.selectedPathLabels();

    return pathLabels.length > 0 ? pathLabels.join(InputNestedSelectComponent.DISPLAY_SEPARATOR) : '';
  }

  public buildPath(pathLabels: string[], option: INormalizedNestedOption): string[] {
    return [...pathLabels, option.label];
  }

  private rebuildOptions(): void {
    this.normalizedOptions = this.normalizeOptions(this.options);
    this.flattenedOptions = this.flattenOptions(this.normalizedOptions);
  }

  private normalizeOptions(options: unknown[]): INormalizedNestedOption[] {
    return (options ?? []).map(option => {
      const label = this.getOptionLabel(option);
      const value = this.getOptionValue(option);
      const children = this.normalizeOptions(this.getOptionChildren(option));

      return {
        label,
        value,
        children
      };
    });
  }

  private flattenOptions(options: INormalizedNestedOption[], parentPath: string[] = []): IFlattenedNestedOption[] {
    const flattened: IFlattenedNestedOption[] = [];

    for (const option of options) {
      const path = [...parentPath, option.label];

      flattened.push({ value: option.value, path });

      if (option.children.length > 0) {
        flattened.push(...this.flattenOptions(option.children, path));
      }
    }

    return flattened;
  }

  private getOptionLabel(option: unknown): string {
    if (this.labelProperty) {
      const extracted = ObjectHelper.extractProperty(option, this.labelProperty);

      return extracted !== undefined && extracted !== null ? String(extracted) : '';
    }

    return option !== undefined && option !== null ? String(option) : '';
  }

  private getOptionValue(option: unknown): unknown {
    if (this.valueProperty) {
      return ObjectHelper.extractProperty(option, this.valueProperty);
    }

    return option;
  }

  private getOptionChildren(option: unknown): unknown[] {
    if (!option || typeof option !== 'object') {
      return [];
    }

    const children = this.childrenProperty ?
      ObjectHelper.extractProperty(option, this.childrenProperty) :
      (option as Record<string, unknown>).children;

    return Array.isArray(children) ? children : [];
  }

  private syncSelectedPathFromValue(value: unknown): void {
    if (value === undefined || value === null) {
      this.selectedPathLabels.set([]);
      return;
    }

    const match = this.flattenedOptions.find(option => ObjectHelper.deepEquals(option.value, value));

    if (match) {
      this.selectedPathLabels.set(match.path);
    } else {
      this.selectedPathLabels.set([]);
    }
  }

  private clearOpenMenus(): void {
    this.openMenus.set([]);
  }
}
